import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormInstance } from 'antd/es/form';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { pickBy } from 'lodash';
import { message } from 'antd';

import {
    IFormValues,
    FormFields,
} from 'interfaces/preliminary-application.interface';
import { api } from 'utils/api';
import { buildFormData } from 'utils/buildFormData';
import { validateAndAppendFiles } from 'utils/validateAndAppendFiles';
import { handleFormError } from 'utils/error-handlers';
import { isValidValue } from 'utils/obj';
import { sleep } from 'utils/sleep';

const LOADER_DELAY_IN_MS: number = 2000;

// Required fields per step for lenient validation
export const STEP_REQUIRED_FIELDS: Record<number, string[]> = {
    0: ['medication_name', 'release_form'], // Step 1
    1: [
        'certificate',
        'applicant_id',
        'notification_email',
        'procedure_id',
        'procedure_desc',
    ], // Step 2
    2: [], // Step 3 - checkboxes are optional
    3: [], // Step 4 - files are optional
    4: ['reg_date'], // Step 5 - application_notes is optional
};

interface UseWizardFormProps {
    form: FormInstance;
    fileList: UploadFile[];
    setFileList: (files: UploadFile[]) => void;
    msgSuccess: (msg: string) => void;
    msgError: (msg: string) => void;
    t: (key: string, options?: any) => string;
    replace: (path: string) => void;
    editRedirectBasePath?: string;
    storageNamespace?: string;
    requiredFieldsMap?: Record<number, string[]>;
}

export const useWizardForm = ({
    form,
    fileList,
    setFileList,
    msgSuccess,
    msgError,
    t,
    replace,
    editRedirectBasePath = '/preliminary-applications/edit',
    storageNamespace = 'wizard',
    requiredFieldsMap = STEP_REQUIRED_FIELDS,
}: UseWizardFormProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFormProcessing, setIsFormProcessing] = useState<boolean>(false);
    const [disSubmit, setDisSubmit] = useState<boolean>(false);
    const storageDraftKey = `${storageNamespace}-draft`;
    const storageFilesKey = `${storageNamespace}-files`;
    const editPathBase = editRedirectBasePath.endsWith('/')
        ? editRedirectBasePath.slice(0, -1)
        : editRedirectBasePath;

    // Get current step from URL, clamped to valid range (0-4)
    const currentStep = useMemo(() => {
        const step = parseInt(searchParams.get('step') || '0', 10);
        return Math.max(0, Math.min(step, 4));
    }, [searchParams]);

    // Reset states handler
    const resetStatesHandler = useCallback(
        (fields?: FormFields, formInstance = form) => {
            setIsFormProcessing(false);
            setDisSubmit(false);
            setFileList([]);

            if (fields) {
                formInstance?.resetFields(fields);
            } else {
                formInstance?.resetFields();
            }
        },
        [form, setFileList]
    );

    // Navigation functions
    const goToStep = useCallback(
        (step: number) => {
            const clampedStep = Math.max(0, Math.min(step, 4));
            setSearchParams({ step: clampedStep.toString() });
        },
        [setSearchParams]
    );

    const goNext = useCallback(async () => {
        // Lenient validation - show warning but allow navigation
        try {
            const fieldsToValidate = requiredFieldsMap[currentStep] ?? [];
            if (fieldsToValidate.length > 0) {
                await form.validateFields(fieldsToValidate);
            }
        } catch (error) {
            // Show warning but don't block navigation
            message.warning(
                t(
                    'preliminary-applications.incomplete_step_warning',
                    'Please fill in required fields'
                )
            );
        }

        // Auto-save to localStorage before navigating (preserves data across steps)
        const allValues = form.getFieldsValue(true);
        localStorage.setItem(storageDraftKey, JSON.stringify(allValues));

        // Navigate regardless of validation
        if (currentStep < 4) {
            goToStep(currentStep + 1);
        }
    }, [currentStep, form, goToStep, storageDraftKey, t]);

    const goPrevious = useCallback(() => {
        // Auto-save to localStorage before navigating back
        const allValues = form.getFieldsValue(true);
        localStorage.setItem(storageDraftKey, JSON.stringify(allValues));

        if (currentStep > 0) {
            goToStep(currentStep - 1);
        }
    }, [currentStep, form, goToStep, storageDraftKey]);

    // Draft save function
    const saveDraft = useCallback(async () => {
        try {
            setDisSubmit(true);
            setIsFormProcessing(true);

            // Get ALL field values, including unmounted ones from other steps
            const values = form.getFieldsValue(true);
            const reg_date = values?.reg_date
                ? dayjs(values?.reg_date).format('DD.MM.YYYY')
                : null;
            const payload = { ...values, reg_date, is_draft: true };
            const filteredValues = pickBy(payload, isValidValue);

            const formData = buildFormData(filteredValues, 'files');

            await validateAndAppendFiles({
                fileList,
                formData,
                form,
                resetStates: (fields, formInstance) =>
                    resetStatesHandler(fields, formInstance),
                message,
                sleep,
                LOADER_DELAY_IN_MS,
            });

            const {
                data: { data },
            } = await api.post(`/api/preliminary-request`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            resetStatesHandler();
            form.resetFields();
            // Clear localStorage after successful save
            localStorage.removeItem(storageDraftKey);
            localStorage.removeItem(storageFilesKey);

            msgSuccess(
                t(
                    'notifications.the_draft_has_been_successfully_saved_take_care_of_sending_the_application',
                    'Draft saved successfully'
                )
            );

            replace(`${editPathBase}/${data?.uuid}`);
        } catch (e) {
            console.error(e);
            handleFormError({
                e,
                form,
                setIsFormProcessing,
                setDisSubmit,
                msgError,
                t,
                navigateToStep: goToStep,
            });
        } finally {
            setDisSubmit(false);
            setIsFormProcessing(false);
        }
    }, [
        form,
        fileList,
        resetStatesHandler,
        msgSuccess,
        msgError,
        t,
        replace,
        goToStep,
        editPathBase,
        storageDraftKey,
        storageFilesKey,
    ]);

    // Final submit function
    const submitFinal = useCallback(
        async (values: IFormValues) => {
            console.log('submit final');
            try {
                setDisSubmit(true);
                setIsFormProcessing(true);

                // Strict validation - validate all required fields
                await form.validateFields();

                // Get ALL field values to ensure we have data from all steps
                const allValues = form.getFieldsValue(true);
                const mergedValues = { ...allValues, ...values };

                const reg_date = mergedValues?.reg_date
                    ? dayjs(mergedValues?.reg_date).format('DD.MM.YYYY')
                    : null;
                const payload = { ...mergedValues, reg_date };
                const filteredValues = pickBy(payload, isValidValue);

                const formData = buildFormData(filteredValues, 'files');

                await validateAndAppendFiles({
                    fileList,
                    formData,
                    form,
                    resetStates: (fields, formInstance) =>
                        resetStatesHandler(fields, formInstance),
                    message,
                    sleep,
                    LOADER_DELAY_IN_MS,
                });

                const {
                    data: { data },
                } = await api.post(`/api/preliminary-request`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                resetStatesHandler();
                form.resetFields();
                localStorage.removeItem(storageDraftKey);
                localStorage.removeItem(storageFilesKey);

                msgSuccess(
                    t(
                        'notifications.the_draft_has_been_successfully_saved_take_care_of_sending_the_application',
                        'Application submitted successfully'
                    )
                );

                replace(`${editPathBase}/${data?.uuid}`);
            } catch (e: any) {
                console.error(e);

                handleFormError({
                    e,
                    form,
                    setIsFormProcessing,
                    setDisSubmit,
                    msgError,
                    t,
                    navigateToStep: goToStep,
                });
            } finally {
                setDisSubmit(false);
                setIsFormProcessing(false);
            }
        },
        [
            form,
            fileList,
            resetStatesHandler,
            msgSuccess,
            msgError,
            t,
            replace,
            goToStep,
            editPathBase,
            storageDraftKey,
            storageFilesKey,
        ]
    );

    return {
        currentStep,
        isFormProcessing,
        disSubmit,
        goToStep,
        goNext,
        goPrevious,
        saveDraft,
        submitFinal,
        resetStatesHandler,
    };
};
